/**
 * Web-Push-Helper (Browser-Seite).
 * Kapselt Notification-Permission, Service-Worker-Registrierung und
 * PushManager-Subscription — inkl. Abgleich mit dem Backend.
 */
import { pushApi } from './api/client';

// Standard-Snippet: VAPID-Public-Key (base64url) → Uint8Array für pushManager.subscribe()
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function getNotificationPermission() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

/**
 * Fragt Berechtigung an (falls nötig), erstellt eine Push-Subscription und
 * meldet sie beim Backend an. Wirft bei Ablehnung/Fehler eine Exception mit
 * verständlicher Meldung.
 */
export async function enablePushNotifications() {
  if (!isPushSupported()) {
    throw new Error('Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Berechtigung für Benachrichtigungen wurde nicht erteilt.');
  }

  const registration = await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const { data } = await pushApi.getVapidPublicKey();
    if (!data.publicKey) {
      throw new Error('Server ist für Push-Benachrichtigungen noch nicht konfiguriert.');
    }
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }

  await pushApi.subscribe(subscription.toJSON());
  return subscription;
}

/**
 * Meldet die Subscription beim Backend ab und beendet sie im Browser.
 */
export async function disablePushNotifications() {
  const subscription = await getCurrentSubscription();
  if (!subscription) return;

  try {
    await pushApi.unsubscribe(subscription.endpoint);
  } finally {
    await subscription.unsubscribe();
  }
}

/**
 * Spielt einen kurzen, dezenten zweitönigen Hinweiston ab — nur wenn die App
 * gerade im Vordergrund geöffnet ist (echte Hintergrund-Push-Benachrichtigungen
 * nutzen stattdessen den System-Benachrichtigungston, siehe README-Hinweis).
 */
export function playNotificationSound() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;

    const playTone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(0.15, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + duration + 0.05);
    };

    playTone(880, 0, 0.15);
    playTone(1175, 0.12, 0.2);
  } catch (e) {
    // Audio-Wiedergabe ist rein kosmetisch — Fehler hier dürfen nie die App stören
    console.warn('Hinweiston konnte nicht abgespielt werden:', e);
  }
}
